import { Button, Checkbox } from '@wfp/react';
import { tooltipStyle } from '@wfp/react';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import ConfirmationModal from 'src/components/ConfirmationModal';
import { resetReportsList } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import {
  PDF_MERGE_MAKE_MERGE,
  PDF_MERGE_MERGE_STATUS,
} from 'src/redux/constants';
import { getPublicationListRequest } from 'src/redux/publicationStatus/actions';
import { getActionAPISource } from 'src/utils';
import {
  calcMergeStatus,
  getLocalStorageItem,
  setLocalStorageItem,
  showNotification,
} from 'src/utils';
import { Header, ReportData } from '../utils/reportsUtils';

interface PublicationMergeManagerProps {
  data?: ReportData[];
  currentPeriod?: string;
  headers?: Header[];
}

interface MergeError {
  [key: string]: string;
}

interface MergeStatusResponse {
  processing?: boolean | null;
  errors?: MergeError[];
}

const MergeErrorsMsg: React.FC<{
  errors: MergeError[];
  publicationData: ReportData[];
}> = ({ errors, publicationData }) => {
  return (
    <>
      {errors.map(error => {
        const objKey = Object.keys(error);
        const reportInfo = publicationData.find(
          item => item.pk === parseInt(objKey?.[0] || '0'),
        );

        return (
          <div key={objKey?.[0]}>
            <li>
              {reportInfo?.project_code}: {error[parseInt(objKey?.[0] || '0')]}
            </li>
            <br />
          </div>
        );
      })}
    </>
  );
};

const PublicationMergeManager: React.FC<PublicationMergeManagerProps> = ({
  data = [],
  currentPeriod,
  headers = [],
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [readyProjectIds, setReadyProjectIds] = useState<string[]>([]);
  const [checkMergeStatus, setCheckMergeStatus] = useState<boolean>(false);

  const publicationData = useMemo(() => {
    return data?.map(reportRow => {
      const mergeStatus = calcMergeStatus(reportRow);
      const { current_status } = reportRow;

      const currentStatus = headers
        .find(item => item.key === current_status)
        ?.title.toLowerCase();

      return {
        ...reportRow,
        current_status: currentStatus,
        merge_status: mergeStatus,
      };
    });
  }, [data, headers]);

  const mergeReadyFiles = useMemo(() => {
    return publicationData
      .filter(({ merge_status }) => merge_status === 'Ready')
      .map(item => item.project_code)
      .filter((code): code is string => !!code);
  }, [publicationData]);

  const toggleReadyProjectId = useCallback(
    (projectId: string) => {
      const projectIdKey = readyProjectIds.indexOf(projectId);
      const readyProjectsIdsClone = [...readyProjectIds];

      if (projectIdKey === -1) {
        readyProjectsIdsClone.push(projectId);
      } else {
        readyProjectsIdsClone.splice(projectIdKey, 1);
      }

      setReadyProjectIds(readyProjectsIdsClone);
    },
    [readyProjectIds, setReadyProjectIds],
  );

  const handleRefreshMergeStatusCheck = useCallback(async () => {
    const url = getActionAPISource(PDF_MERGE_MERGE_STATUS);

    try {
      const response = await apiCallAsync(url, null, 'get');
      const { processing = null, errors = [] }: MergeStatusResponse =
        response?.data || {};
      if (processing) return;

      if (isEmpty(errors)) {
        showNotification('Merge completed', 'success', {});
        dispatch(resetReportsList());
        dispatch(
          getPublicationListRequest({
            period: currentPeriod
              ? currentPeriod
              : location.pathname.replace(/[^0-9]/g, ''),
          }),
        );
      } else {
        showNotification(
          <MergeErrorsMsg errors={errors} publicationData={publicationData} />,
          'error',
          {},
        );
      }
      setCheckMergeStatus(false);
      setLocalStorageItem('waiting_merge_result', '-', false);
    } catch (error) {
      console.log(error);
    }
  }, [currentPeriod, location.pathname, dispatch]);

  useEffect(() => {
    const mergePending = getLocalStorageItem('waiting_merge_result');
    if (mergePending) setCheckMergeStatus(true);
  }, []);

  useEffect(() => {
    const timeoutID: NodeJS.Timeout | null = checkMergeStatus
      ? setInterval(() => {
          handleRefreshMergeStatusCheck();
        }, 3000)
      : null;
    if (!checkMergeStatus && timeoutID) {
      clearInterval(timeoutID);
    }

    return () => {
      if (timeoutID) clearInterval(timeoutID);
    };
  }, [checkMergeStatus, handleRefreshMergeStatusCheck]);

  const mergeFiles = useCallback(async () => {
    const url = getActionAPISource(PDF_MERGE_MAKE_MERGE);

    const pkList = publicationData
      .filter(
        item =>
          item.project_code && readyProjectIds.includes(item.project_code),
      )
      .map(item => item.pk)
      .filter((pk): pk is number => !!pk);

    try {
      const response = await apiCallAsync(url, { pk_list: pkList }, 'post');

      if (response?.data !== 'Merge started') throw Error;

      showNotification('Merge started', 'success', {});
      setLocalStorageItem('waiting_merge_result', '+', false);
      setCheckMergeStatus(true);
    } catch (error) {
      console.error(error);
      showNotification(
        'Error while starting merge. Please try later',
        'error',
        {},
      );
    }

    setConfirmationModal(false);
  }, [publicationData, readyProjectIds]);

  const handleMergeReady = useCallback(async () => {
    const url = getActionAPISource(PDF_MERGE_MERGE_STATUS);

    try {
      const response = await apiCallAsync(url, null, 'get');
      const { processing = null }: MergeStatusResponse = response?.data || {};

      if (processing) {
        showNotification('Another merge is in progress', 'error', {});
        return;
      }

      setConfirmationModal(true);
    } catch (error) {
      console.error(error);
      showNotification(
        'Error on checking merge status. Please try later',
        'error',
        {},
      );
    }
  }, []);

  const isDisabled = mergeReadyFiles.length === 0 || checkMergeStatus;

  const mergeButton = (
    <Button
      disabled={isDisabled}
      onClick={handleMergeReady}
      data-test-id="merge-ready-button"
    >
      Merge ready
    </Button>
  );

  return (
    <>
      {isDisabled ? (
        <Tooltip
          title="No records available"
          trigger="mouseenter"
          {...tooltipStyle}
          theme="light"
          position="bottom"
          disabled={mergeReadyFiles.length > 0}
        >
          {mergeButton}
        </Tooltip>
      ) : (
        <div>{mergeButton}</div>
      )}

      <ConfirmationModal
        open={confirmationModal}
        onSubmit={mergeFiles}
        onClose={() => setConfirmationModal(false)}
        heading="Confirm Merge"
        primaryButtonDisabled={!readyProjectIds?.length}
        primaryButtonText="Confirm"
        content={
          <div className="merge-ready-modal">
            These ACR previews will be generated: <br />
            <br />
            {mergeReadyFiles.map(item => (
              <Checkbox
                id={item}
                key={item}
                labelText={item}
                onChange={() => toggleReadyProjectId(item)}
                checked={readyProjectIds.includes(item)}
              />
            ))}
            <br />
            Do you want to proceed with merge?
          </div>
        }
      />
    </>
  );
};

export default PublicationMergeManager;
